import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncAction } from '../../hooks/useAsyncAction';

function generateTptSlug(title) {
  const base = title.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const rand = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${base}-${rand}`;
}

export default function AdminNovelsPage() {
  const navigate = useNavigate();
  const [novels, setNovels] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', author: '', grade_range: '', tpt_slug: '' });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const { loading, run: runLoad } = useAsyncAction();
  const { loading: saving, error, run: runAdd } = useAsyncAction();

  async function load() {
    await runLoad(async () => {
      const res = await fetch('/api/admin/novels', { credentials: 'include' });
      const data = await res.json();
      setNovels(Array.isArray(data) ? data : []);
    });
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(novel) {
    await fetch(`/api/admin/novels/${novel.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ active: !novel.active }),
    });
    load();
  }

  async function handleAdd(e) {
    e.preventDefault();
    await runAdd(async () => {
      const res = await fetch('/api/admin/novels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...form, words: [] }),
      });
      const data = await res.json();
      if (!res.ok) {
        // allow 0 words — we'll add them on the edit page
        if (data.error?.includes('25 word')) {
          throw new Error('Note: add vocabulary words on the novel edit page.');
        }
        throw new Error(data.error || 'Failed to add novel');
      }
      setShowAdd(false);
      setForm({ title: '', author: '', grade_range: '', tpt_slug: '' });
      setSlugManuallyEdited(false);
      navigate(`/admin/novels/${data.id}`);
    });
  }

  return (
    <>
      <div style={s.header}>
        <h1 style={s.h1}>Novels</h1>
        <button onClick={() => { setShowAdd(v => !v); setForm({ title: '', author: '', grade_range: '', tpt_slug: '' }); setSlugManuallyEdited(false); }} style={s.addBtn}>
          {showAdd ? 'Cancel' : '+ Add Novel'}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} style={s.addForm}>
          <h3 style={s.formHeading}>New Novel</h3>
          {[
            { field: 'title', required: true },
            { field: 'author', required: true },
            { field: 'grade_range', required: false },
            { field: 'tpt_slug', required: false },
          ].map(({ field, required }) => (
            <div key={field} style={s.field}>
              <label style={s.label}>{field.replace(/_/g, ' ')}</label>
              <input
                value={form[field]}
                onChange={e => {
                  const val = e.target.value;
                  if (field === 'title') {
                    setForm(f => ({
                      ...f,
                      title: val,
                      tpt_slug: slugManuallyEdited ? f.tpt_slug : (val.trim() ? generateTptSlug(val) : ''),
                    }));
                  } else if (field === 'tpt_slug') {
                    setSlugManuallyEdited(true);
                    setForm(f => ({ ...f, tpt_slug: val }));
                  } else {
                    setForm(f => ({ ...f, [field]: val }));
                  }
                }}
                required={required}
                style={s.input}
                placeholder={field === 'tpt_slug' ? 'auto-generated from title' : ''}
              />
            </div>
          ))}
          {error && <p style={s.error}>{error}</p>}
          <p style={s.hint}>You'll add vocabulary words on the next screen.</p>
          <button type="submit" disabled={saving} style={s.saveBtn}>
            {saving ? 'Creating…' : 'Create & Add Words →'}
          </button>
        </form>
      )}

      {loading ? <p style={s.muted}>Loading…</p> : (
        <table style={s.table}>
          <thead>
            <tr>
              {['Title', 'Author', 'Grade', 'Words', 'Status', ''].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {novels.map(n => (
              <tr key={n.id} style={s.tr}>
                <td style={s.td}>{n.title}</td>
                <td style={s.td}>{n.author}</td>
                <td style={s.td}>{n.grade_range || '—'}</td>
                <td style={{ ...s.td, color: n.word_count < 40 ? '#dc2626' : '#15803d', fontWeight: '600' }}>
                  {n.word_count}{n.word_count < 40 ? ' ⚠' : ''}
                </td>
                <td style={s.td}>
                  <span style={{ ...s.badge, ...(n.active ? s.badgeGreen : s.badgeGray) }}>
                    {n.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={s.td}>
                  <button onClick={() => navigate(`/admin/novels/${n.id}`)} style={s.linkBtn}>Edit</button>
                  <button onClick={() => toggleActive(n)} style={s.linkBtn}>
                    {n.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  h1: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 },
  addBtn: { padding: '8px 16px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  addForm: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
    padding: '24px', marginBottom: '24px' },
  formHeading: { margin: '0 0 16px', fontSize: '16px', color: '#111827' },
  field: { marginBottom: '12px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', boxSizing: 'border-box' },
  hint: { fontSize: '13px', color: '#6b7280', margin: '0 0 12px' },
  error: { color: '#dc2626', fontSize: '13px', margin: '0 0 12px' },
  saveBtn: { padding: '9px 20px', backgroundColor: '#16a34a', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  muted: { color: '#6b7280' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff',
    border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' },
  th: { padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151' },
  badge: { padding: '2px 8px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  badgeGreen: { backgroundColor: '#dcfce7', color: '#15803d' },
  badgeGray: { backgroundColor: '#f3f4f6', color: '#6b7280' },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', fontSize: '13px',
    cursor: 'pointer', marginRight: '8px', padding: '2px 0', fontWeight: '500' },
};
